/* eslint-disable default-param-last */
import createSelectors from './createSelectors.js'

// State is formed like this 
// {
//   resourceId : 
//     requestId, // id of the request data,
//     status : 'loading',
//     error : { },
//     limit : <number>,
//     count : <number>,
//     offset : <number>,
//     next : <token>
//     [namePlural] : [
//       { ...item0 }, { ...item1 }
//     ],
//     // FUTURE? lookup : { <id> : <item> }
//     [name]Status : {
//       id : { status, error }
//     }
//     
// }
function create( options ) {
  const { 
    initialState = { },
    name, // name of an item. eg: user
    namePlural, // plural name of the items. eg: users
    idActionKey = 'id', // the key in the actions to get the item's unique id 
    idKey = 'id', // the key in the item to get the item's unique id 
    resourceKey = 'resourceId', // the key used to get the unique id out of the action
    actionHandlers = null, // function( state, action, createState ). If this returns a different 
    // state object, the default reducers will be ignored
    countKey = 'count',
    totalKey = 'total',
    limitKey = 'limit',
    offsetKey = 'offset',
    nextKey = 'next',
    itemsKey = 'items',
    itemKey = 'item',
    extraKey = 'extra',
    defaultStateKey,
    constantPrefix
  } = options

  if ( constantPrefix == null ) {
    throw new Error( 'constantPrefix needs to be defined' )
  }

  const STATUS = `${constantPrefix}.status`
  const ADD = `${constantPrefix}.add`
  const UPDATE = `${constantPrefix}.update`
  const REMOVE = `${constantPrefix}.remove`
  const LIST = `${constantPrefix}.list`

  const namesKey = namePlural == null ? `${name}s` : namePlural
  const statusKey = `${name}Status`

  function createState( curr, itemId, status = null, error = null ) {

    if ( curr == null ) {
      return { [namesKey] : [ ], [statusKey] : { }, status, error, count : 0, offset : 0, limit : 0, next : null }
    }
    if ( itemId == null ) {
      return { 
        count : 0,
        offset : 0,
        limit : 0,
        next : null, 
        [namesKey] : [ ],
        [statusKey] : { },
        ...curr,
        status,
        error 
      }
    } 
  
    const rtn = { ...curr, [statusKey] : { ...curr[statusKey] } }
    if ( status == null ) {
      delete rtn[statusKey][itemId]
    } else {
      rtn[statusKey][itemId] = { status, error }
    }
    
    return rtn
  }

  function resourceList( state = initialState, action ) {
  
    if ( typeof actionHandlers === 'function' ) {
      const nState = actionHandlers( state, action, createState )
      if ( nState !== state ) {
        return nState
      }
    }

    switch ( action.type ) {
      case STATUS: {
        const { [resourceKey] : resourceId, [idActionKey] : itemId, status, error = null } = action
        const nState = { ...state }
        nState[resourceId] = createState( state[resourceId], itemId, status, error )
        return nState
      }

      case LIST: {
        const { 
          requestId, // used to determine if this is a new/same/refresh request
          [resourceKey] : resourceId, 
          [itemsKey] : items, 
          [countKey] : count = 0,
          [totalKey] : total = 0,
          [nextKey] : next = null, 
          [limitKey] : limit = 0, 
          [offsetKey] : offset = 0,
          [extraKey] : extra,
        } = action

        const nState = { ...state }
        nState[resourceId] = createState( state[resourceId] )
        nState[resourceId].count = count
        nState[resourceId].total = total
        nState[resourceId].next = next
        nState[resourceId].limit = limit
        nState[resourceId].extra = { ...nState?.[resourceId]?.extra, ...extra }
        if ( nState[resourceId].requestId !== requestId ) {
          nState[resourceId][namesKey] = [ ]
          nState[resourceId].requestId = requestId
        }
    
        nState[resourceId][namesKey] = [ ...nState[resourceId][namesKey] ]
        items.forEach( ( item, index ) => {
          nState[resourceId][namesKey][index + offset] = item
        } )
        return nState
      }

      case ADD: {
        const { [resourceKey] : resourceId, [itemKey] : item, [extraKey] : extra } = action
        const nState = { ...state }
        nState[resourceId] = createState( state[resourceId], item[idKey] )
        nState[resourceId].extra = { ...nState?.[resourceId]?.extra, ...extra }
        // TODO : this is incorrect as item might not be the next element in the list!
        const idx = nState[resourceId][namesKey].findIndex( i => i.id === item.id )
        if ( idx < 0 ) {
          nState[resourceId][namesKey] = [ ...nState[resourceId][namesKey], item ]
          nState[resourceId].count++
          nState[resourceId].total++
        } else {
          nState[resourceId][namesKey] = [ ...nState[resourceId][namesKey] ]
          nState[resourceId][namesKey][idx] = item
        }

        return nState
      }

      case REMOVE: {
        const { [resourceKey] : resourceId, [idActionKey] : itemId, [extraKey] : extra } = action
        const nState = { ...state }
        nState[resourceId] = createState( state[resourceId], itemId )
        nState[resourceId].extra = { ...nState?.[resourceId]?.extra, ...extra }
        // eslint-disable-next-line eqeqeq
        if ( nState[resourceId][namesKey].find( u => u[idKey] == itemId ) !== undefined ) {
          // eslint-disable-next-line eqeqeq
          nState[resourceId][namesKey] = nState[resourceId][namesKey].filter( u => u[idKey] != itemId )
          nState[resourceId].count--
          nState[resourceId].total--
        } else {
          // not change, return state
          return state
        }

        return nState
      }

      case UPDATE: {
        const { [resourceKey] : resourceId, [itemKey] : item, [extraKey] : extra } = action
        const nState = { ...state }
        nState[resourceId] = createState( state[resourceId], item[idKey] )
        nState[resourceId].extra = { ...nState?.[resourceId]?.extra, ...extra }
        nState[resourceId][namesKey] = nState[resourceId][namesKey].map( ( itm ) => {
        // eslint-disable-next-line eqeqeq
          if ( itm[idKey] != item[idKey] ) {
            return itm
          }
          return item
        } )
        return nState
      }

      default: {
        return state
      }
    }
  }

  function getStatus( state, resourceId, itemId ) {
    if ( state?.[resourceId] ) {
      if ( itemId != null ) {
        const rs = state[resourceId].userStatus
        return rs && rs[itemId] && rs[itemId].status ? rs[itemId].status : null
      }
      return state[resourceId].status
    }
    return null
  }

  function getError( state, resourceId, itemId ) {

    if ( state?.[resourceId] ) {
      if ( itemId != null ) {
        const rs = state[resourceId].userStatus
        return rs && rs[itemId] && rs[itemId].error ? rs[itemId].error : null
      }
      return state[resourceId].error
    }
    return null
  }

  function getItems( state, resourceId ) {
    if ( state?.[resourceId]?.[namesKey] ) {
      return state[resourceId][namesKey]
    }
    return []
  }

  function get( state, resourceId ) {
    return state?.[resourceId] || null
  }

  function getCount( state, resourceId ) {
    if ( state?.[resourceId] ) {
      return state[resourceId].count || 0
    }
    return 0
  }

  function getTotal( state, resourceId ) {
    if ( state?.[resourceId] ) {
      return state[resourceId].total || 0
    }
    return 0
  }

  function getOffset( state, resourceId ) {
    if ( state?.[resourceId] ) {
      return state[resourceId].offset || 0
    }
    return 0
  }

  function getLimit( state, resourceId ) {
    if ( state?.[resourceId] ) {
      return state[resourceId].limit
    }
    return -1
  }

  function getNext( state, resourceId ) {
    if ( state?.[resourceId] ) {
      return state[resourceId].next || null
    }
    return null
  }

  /**
   * Returns true if the requestId is not the current requestId or if 
   * the item is undefined
   */
  function needsLoading( resourceState, requestId, index ) {
    if ( resourceState && resourceState.requestId === requestId ) {
      return resourceState[namesKey][index] === undefined
    }
    return true
  }

  // resourceList.createState = createState
  resourceList.getStatus = getStatus
  resourceList.getError = getError
  resourceList.get = get
  resourceList.needsLoading = needsLoading
  const cNames = `${namesKey[0].toUpperCase()}${namesKey.substring( 1 )}`
  const cName = `${name[0].toUpperCase()}${name.substring( 1 )}`
  resourceList[`get${cNames}`] = getItems
  resourceList[`get${cName}Count`] = getCount
  resourceList[`get${cName}Total`] = getTotal
  resourceList[`get${cName}Offset`] = getOffset
  resourceList[`get${cName}Limit`] = getLimit
  resourceList[`get${cName}Next`] = getNext

  resourceList.createSelectors = ( key, createSetStateKey = false ) => {
    return { 
      ...createSelectors( key, {
        get, 
        getStatus, 
        getError, 
        [`get${cNames}`] : getItems,
        [`get${cName}Count`] : getCount,
        [`get${cName}Total`] : getTotal,
        [`get${cName}Offset`] : getOffset,
        [`get${cName}Limit`] : getLimit,
        [`get${cName}Next`] : getNext,
      }, createSetStateKey ),
      needsLoading }
  }

  // Set up initial defaults to make methods available immediately with default state key
  const selectors = resourceList.createSelectors( defaultStateKey, true )
  Object.keys( selectors ).forEach( ( key ) => { resourceList[key] = selectors[key] } )

  return resourceList
}

export default {
  create
}
